import { el } from "../../utils/dom";

export interface MultiSelectDropdownOptions {
    availableTags: string[];
    selectedTags: string[];
    onChange: (newTags: string[]) => void;
    placeholder?: string;
}

/**
 * Create a multi-select dropdown component for tag selection
 */
export function createMultiSelectDropdown(options: MultiSelectDropdownOptions): HTMLElement {
    const { availableTags, selectedTags, onChange, placeholder = "Select tags..." } = options;

    let isOpen = false;
    let isListenerActive = false;

    // Container
    const container = el("div", {
        class: "multiselect-container"
    });

    // Display area (chips + chevron)
    const display = el("div", {
        class: "multiselect-display"
    });

    // Dropdown panel (checkboxes)
    const dropdown = el("div", {
        class: "multiselect-dropdown"
    });

    // Render display area (chips)
    function renderDisplay() {
        display.innerHTML = "";

        if (selectedTags.length === 0) {
            // Show placeholder
            const placeholderSpan = el("span", {
                class: "multiselect-placeholder"
            }, placeholder);
            display.appendChild(placeholderSpan);
        } else {
            // Show chips
            for (const tag of selectedTags) {
                const chip = el("span", {
                    class: "multiselect-chip"
                }, tag);

                const removeBtn = el("span", {
                    class: "multiselect-chip-remove"
                }, "×");

                removeBtn.addEventListener("click", (e) => {
                    e.stopPropagation();
                    const newTags = selectedTags.filter(t => t !== tag);
                    onChange(newTags);
                });

                chip.appendChild(removeBtn);
                display.appendChild(chip);
            }
        }

        // Add chevron
        const chevron = el("span", {
            class: "multiselect-chevron"
        }, "▼");
        display.appendChild(chevron);
    }

    // Render dropdown options
    function renderDropdown() {
        dropdown.innerHTML = "";

        if (availableTags.length === 0) {
            const emptyMsg = el("div", {
                class: "multiselect-empty"
            }, "No tags available. Create tags in Settings.");
            dropdown.appendChild(emptyMsg);
            return;
        }

        for (const tag of availableTags) {
            const isSelected = selectedTags.includes(tag);

            const checkbox = el("input", {
                type: "checkbox",
                checked: isSelected ? true : undefined
            }) as HTMLInputElement;

            checkbox.addEventListener("change", () => {
                const newTags = checkbox.checked
                    ? [...selectedTags, tag]
                    : selectedTags.filter(t => t !== tag);
                onChange(newTags);
            });

            const option = el("div", {
                class: "multiselect-option"
            });
            option.append(checkbox, tag);

            option.addEventListener("click", (e) => {
                if (e.target !== checkbox) {
                    checkbox.checked = !checkbox.checked;
                    checkbox.dispatchEvent(new Event("change"));
                }
            });

            dropdown.appendChild(option);
        }
    }

    // Toggle dropdown
    function toggleDropdown() {
        isOpen = !isOpen;
        if (isOpen) {
            container.classList.add("open");
        } else {
            container.classList.remove("open");
        }
    }

    // Close dropdown when clicking outside
    function handleOutsideClick(e: MouseEvent) {
        if (!container.contains(e.target as Node)) {
            if (isOpen) {
                toggleDropdown();
                cleanupListener();
            }
        }
    }

    // Cleanup listener helper
    function cleanupListener() {
        if (isListenerActive) {
            document.removeEventListener("click", handleOutsideClick);
            isListenerActive = false;
        }
    }

    // Container click to toggle
    container.addEventListener("click", (e) => {
        e.stopPropagation();
        toggleDropdown();

        if (isOpen) {
            // Clean up any existing listener first
            cleanupListener();

            // Add outside click listener
            setTimeout(() => {
                if (isOpen && !isListenerActive) {
                    document.addEventListener("click", handleOutsideClick);
                    isListenerActive = true;
                }
            }, 0);
        } else {
            cleanupListener();
        }
    });

    // Initial render
    renderDisplay();
    renderDropdown();

    container.append(display, dropdown);

    return container;
}
